import {
	LocationSelector,
	RollsetSelector,
	SectionSelector,
} from "@/shared/selectors";
import { EdgerPassSelector } from "@/shared/selectors/EdgerPassSelector";
import { CheckCircle } from "@mui/icons-material";
import {
	Button,
	FormControl,
	FormControlLabel,
	Grid2,
	Radio,
	RadioGroup,
	TextField,
	Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import moment from "moment";
import { useEffect, useState } from "react";
import { Question } from "./models/Questions";
import { EdgerRollPass } from "@/pages/catalogs/models/EdgerRoll";

interface QuestionCardRoll {
	id: number | null;
	name: string | null;
	type: string | null;
}

interface QuestionCardProps {
	question: Question;
	index: string;
	answer: any;
	onAnswerChange: (answer: any) => void;
	disabled?: boolean;
	standId: number | null;
	standName: string | null;
	sectionId: number | null;
	sectionName: string | null;
	filterStands?: string | null;
	topRoll?: QuestionCardRoll;
	bottomRoll?: QuestionCardRoll;
	rollsPasses?: { top: EdgerRollPass[] | null; bottom: EdgerRollPass[] | null };
}

export const QuestionCard = ({
	question,
	index,
	answer,
	onAnswerChange,
	disabled = false,
	standId,
	standName,
	sectionId,
	sectionName,
	filterStands = null,
	topRoll,
	bottomRoll,
	rollsPasses,
}: QuestionCardProps) => {
	const [localValue, setLocalValue] = useState(answer);

	useEffect(() => {
		setLocalValue(answer);
	}, [answer]);

	const handleAnswerChange = (newAnswer: any) => {
		setLocalValue(newAnswer);
		onAnswerChange(newAnswer);
	};

	const handleTextChange = (newValue: any) => {
		setLocalValue(newValue);
	};

	const handleTextBlur = () => {
		if (localValue !== answer) {
			onAnswerChange(localValue);
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter") {
			e.preventDefault();
			if (localValue !== answer) {
				onAnswerChange(localValue);
			}
		}
	};

	const renderInput = () => {
		switch (question.type) {
			case "date":
				return (
					<Grid2 container spacing={1} alignItems="center">
						<Grid2 size={{ xs: 8, md: 10 }}>
							<DatePicker
								label="Select Date"
								value={answer ? moment(answer) : null}
								onChange={(date) =>
									handleAnswerChange(date?.toDate().toISOString())
								}
								disabled={disabled}
								slots={{
									textField: (params) => <TextField {...params} fullWidth />,
								}}
							/>
						</Grid2>
						<Grid2 size={{ xs: 4, md: 2 }}>
							<Button
								variant="contained"
								color="primary"
								onClick={() => handleAnswerChange(new Date().toISOString())}
								disabled={disabled}
								size="medium"
								fullWidth
								data-test-id="date-now-button"
							>
								NOW
							</Button>
						</Grid2>
					</Grid2>
				);

			case "number":
				return (
					<TextField
						type="number"
						value={localValue || ""}
						onChange={(e) => handleTextChange(Number(e.target.value))}
						onBlur={handleTextBlur}
						onKeyDown={handleKeyDown}
						disabled={disabled}
						fullWidth
						size="small"
						variant="outlined"
						slotProps={{
							htmlInput: { "data-test-id": "question-number" },
						}}
					/>
				);

			case "text":
				return (
					<TextField
						value={localValue || ""}
						onChange={(e) => handleTextChange(e.target.value)}
						onBlur={handleTextBlur}
						onKeyDown={handleKeyDown}
						disabled={disabled}
						fullWidth
						size="small"
						variant="outlined"
						slotProps={{
							htmlInput: { "data-test-id": "question-text" },
						}}
					/>
				);

			case "options":
				return (
					<FormControl component="fieldset" fullWidth>
						<RadioGroup
							value={answer || ""}
							onChange={(e) => handleAnswerChange(e.target.value)}
						>
							{question.options?.map((option) => (
								<FormControlLabel
									key={option}
									value={option}
									control={<Radio disabled={disabled} color="primary" />}
									label={option}
									data-test-id={`option-${option}`}
								/>
							))}
						</RadioGroup>
					</FormControl>
				);

			case "section":
				return (
					<SectionSelector
						label="Section"
						value={answer}
						onChange={(value: number | null) => handleAnswerChange(value)}
						disabled={disabled}
					/>
				);

			case "boolean":
				return (
					<FormControl component="fieldset" fullWidth>
						<RadioGroup
							value={answer}
							onChange={(e) => handleAnswerChange(e.target.value)}
						>
							<FormControlLabel
								value="Yes"
								control={<Radio disabled={disabled} color="primary" />}
								label="Yes"
								data-test-id="yes-option"
							/>
							<FormControlLabel
								value="No"
								control={<Radio disabled={disabled} color="primary" />}
								label="No"
								data-test-id="no-option"
							/>
						</RadioGroup>
					</FormControl>
				);

			case "stand":
				return (
					<LocationSelector
						label={"Stand"}
						value={answer}
						onChange={(value: number) => handleAnswerChange(value)}
						disabled={disabled}
						onlyStands
						filterStands={filterStands}
					/>
				);

			case "rollset":
				return (
					<RollsetSelector
						value={answer}
						onChange={(value) => handleAnswerChange(value)}
						disabled={disabled}
						standId={standId}
						standName={standName}
						sectionId={sectionId}
					/>
				);

			case "rollsPass":
				if ((topRoll?.type != "E" && bottomRoll?.type != "E") || !rollsPasses) {
					return (
						<Typography variant="body2" color="textSecondary">
							Not applicable (rolls are not edger)
						</Typography>
					);
				}
				return (
					<EdgerPassSelector
						value={Number(answer)}
						onChange={(value) => handleAnswerChange(value)}
						withShortcut
						passes={rollsPasses}
						rollNames={[topRoll?.name, bottomRoll?.name]}
						rollIds={[topRoll?.id, bottomRoll?.id]}
						sectionName={sectionName}
						disabled={disabled}
					/>
				);

			default:
				return null;
		}
	};

	return (
		<Grid2 style={{ padding: 20 }} data-test-id={`question-${question.key}`}>
			<Grid2 direction="row" style={{ display: "flex", gap: 10 }}>
				{answer && (
					<CheckCircle
						fontSize="small"
						style={{ color: "green", alignItems: "center", marginTop: 4 }}
					/>
				)}
				<Typography variant="h6" gutterBottom>
					{index}. {question.text}
				</Typography>
			</Grid2>
			{renderInput()}
		</Grid2>
	);
};
